import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import formatMetric from "utils/formatMetric";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

import jsDig from 'utils/jsDig';

export default function LiteLineChart(props) {
  console.log(props)
  const themeToggleRef = useRef(null);

  // Need to get the theme from the DOM
  const [darkMode, setDarkMode] = useState(document.body.getAttribute('data-theme') === 'dark');

  useEffect(() => {
    themeToggleRef.current = document.getElementById('theme-toggle');
    const handleThemeChange = () => {
      setDarkMode(themeToggleRef.current.checked);
    };

    if (themeToggleRef.current) {
      themeToggleRef.current.addEventListener('change', handleThemeChange);
    }

    // Check initial state
    if (themeToggleRef.current) {
      setDarkMode(themeToggleRef.current.checked);
    }

    return () => {
        if (themeToggleRef.current) {
          themeToggleRef.current.removeEventListener('change', handleThemeChange);
        }
    };
  }, []);

  const getChartOptions = (darkMode) => {
    let xScalesConfig = {
      ticks: {
        maxTicksLimit: 5,
        maxRotation: 0,
        autoSkip: true,
        color: darkMode ? '#eae6e1' : '#434248',
        font: {
          size: 12, 
          family: '"DM Mono", arial, sans-serif',
        },
      },
      grid: {
        display: true,
      },
    }

    let yScalesConfig = {
      beginAtZero: props.metric == "stacked",
      stacked: props.metric == "stacked",
      ticks: {
        color: darkMode ? '#eae6e1' : '#434248',
        font: {
          size: 12, 
          family: '"DM Mono", arial, sans-serif',
        },
        callback: function(value, index, values) {
          return ` ${formatMetric(props.metric, value, "tick")}`;
        },
      },
      grid: {
        drawBorder: false,
      },
    }
    if (props.metric == "stacked") {
      yScalesConfig["min"] = 0;
      yScalesConfig["max"] = 100;
      yScalesConfig["ticks"]["stepSize"] = 20;
    }

    return {
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: { 
            padding: 20,
            color: darkMode ? '#eae6e1' : '#434248',
            font: {
              size: 16, 
              family: '"DM Mono", arial, sans-serif',
            }
          }
        },
        title: {
          display: false,
        },
        tooltip: {
          titleFont: {
            size: 16,
            family: '"DM Mono", arial, sans-serif',
          },
          bodyFont: {
            size: 16,
            family: '"DM Mono", arial, sans-serif',
          },
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || '';
    
              if (label) {
                label += ': ';
              }

              if (props.metric == "stacked" && props.tooltipData) {
                // Show the actual metric which is passed in through props.tooltipData
                label += formatMetric(props.tooltipMetric, props.tooltipData[context.dataset.label][context.dataIndex], "");
              } else if (context.parsed.y !== null) {
                label += formatMetric(props.metric, context.parsed.y, "");
              }
              return label;
            }
          }
        },
      },
      scales: {
        x: xScalesConfig,
        y: yScalesConfig,
      },
    }
  };
  
  const labels = props.labels;
  let data = {
    labels,
    datasets: props.datasetNames.map((name, index) => ({
      label: name,
      fill: props.metric == "stacked",
      pointRadius: props.metric == "stacked" ? 0 : 3,
      data: labels.map((day, index) => jsDig([name, index], props.data)),
      borderColor: getLineColor(index, name, darkMode),
      backgroundColor: getLineColor(index, name, darkMode),
      borderJoinStyle: "round",
    })),
  };

  return (
    <div className="lite-metric-chart-inner" style={props.fixedHeight ? {height: `${props.fixedHeight}px`} : null}>
      <Line options={getChartOptions(darkMode)} data={data} />
    </div>
  )
}

function getLineColor(index, label, darkMode) {
  // TODO: Colors aren't finalized yet but we can modify these to make it easier to understand which traffic is from which brand
  const newLabel = String(label).toLowerCase();
  const thisYear = new Date().getFullYear();
  const lastYear = thisYear - 1;

  if (String(thisYear) == newLabel) {
    return "#1E9C77";
  }
  if (String(lastYear) == newLabel) {
    return "#7E7D7E";
  }
  if (newLabel == "other") {
    return "#7E7D7E";
  }
  if (newLabel == "snapchat") {
    return "#BF9E40";
  }
  if (newLabel == "facebook") {
    return "#2488BB";
  }
  if (newLabel == "instagram") {
    return "#EE884F";
  }
  if (newLabel == "youtube") {
    return "#C75B5B";
  }
  if (newLabel == "twitter") {
    return "#22BEBE";
  }
  if (newLabel == "tiktok") {
    return "#CB55B1";
  }
  if (newLabel == "linkedin") {
    return "#1E9C77";
  }
  
  const lightColors = ["#1E9C77", "#2488BB", "#BF9E40", "#6746EA", "#22BEBE", "#CB55B1", "#FFD23F", "#7E7D7E", "#C75B5B"];;
  const darkColors = ["#1E9C77", "#2488BB", "#BF9E40", "#6746EA", "#22BEBE", "#CB55B1", "#FFD23F", "#7E7D7E", "#C75B5B"];;
  return darkMode ? darkColors[index % darkColors.length] : lightColors[index % lightColors.length];
}